import '../styles/SignUpEnrollmentPanel.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const FIELDS = [
  { name: 'fullName', label: 'Full Legal Name', autoComplete: 'name', type: 'text' },
  { name: 'email', label: 'Email Address', autoComplete: 'email', type: 'email' },
  { name: 'password', label: 'Password', autoComplete: 'new-password', type: 'password' },
  { name: 'confirmPassword', label: 'Confirm Password', autoComplete: 'new-password', type: 'password' },
];

const READOUTS = [
  { key: 'Identity', value: 'Bound to the account on enrollment' },
  { key: 'Account record', value: 'Created on submission' },
  { key: 'Balance', value: 'Check account balance' },
  { key: 'Transfers', value: 'Transfer funds' },
  { key: 'History', value: 'View transfer history' },
];

function validateField(name, value, allValues) {
  const value_ = typeof value === 'string' ? value : '';
  switch (name) {
    case 'fullName':
      if (!value_.trim()) return 'Enter the full legal name for this account.';
      if (value_.trim().length < 2) return 'Enter at least 2 characters.';
      return '';
    case 'email': {
      if (!value_.trim()) return 'Enter an email address.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value_.trim())) {
        return 'Enter a valid email address, for example name@example.com.';
      }
      return '';
    }
    case 'password':
      if (!value_) return 'Enter a password.';
      if (value_.length < 8) return 'Use at least 8 characters.';
      return '';
    case 'confirmPassword':
      if (!value_) return 'Re-enter the password.';
      if (value_ !== (allValues.password || '')) return 'Passwords do not match.';
      return '';
    default:
      return '';
  }
}

export default function SignUpEnrollmentPanel({ onSubmit }) {
  const { data, refresh } = useProjectCollection('client_accounts');

  const [values, setValues] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [reveal, setReveal] = useState({ password: false, confirmPassword: false });
  const [status, setStatus] = useState('idle');
  const [submissionError, setSubmissionError] = useState('');
  const [createdId, setCreatedId] = useState('');

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleBlur = (name) => {
    const message = validateField(name, values[name], values);
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {};
    FIELDS.forEach((field) => {
      const message = validateField(field.name, values[field.name], values);
      if (message) nextErrors[field.name] = message;
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus('idle');
      setSubmissionError('');
      return;
    }

    if (typeof onSubmit !== 'function') {
      setStatus('unavailable');
      setSubmissionError('Enrollment is not available in this preview.');
      return;
    }

    setStatus('submitting');
    setSubmissionError('');

    try {
      const result = await onSubmit(values);
      if (result && result.success) {
        const id = result.account && result.account.id ? result.account.id : '';
        setCreatedId(id);
        setStatus('success');
        setValues({ fullName: '', email: '', password: '', confirmPassword: '' });
        setErrors({});
        await refresh();
      } else {
        setStatus('error');
        setSubmissionError(
          result && result.error
            ? result.error
            : 'The enrollment submission could not be completed. Your details have been kept — try again.'
        );
      }
    } catch (err) {
      setStatus('error');
      setSubmissionError(
        (err && err.message) ||
          'The enrollment submission could not be completed. Your details have been kept — try again.'
      );
    }
  };

  const submitting = status === 'submitting';
  const succeeded = status === 'success';

  return (
    <section className="sep-root" aria-labelledby="sep-title">
      <div className="sep-grid">
        <div className="sep-panel">
          <header className="sep-head">
            <p className="sep-micro">Client Enrollment</p>
            <h2 className="sep-title" id="sep-title">
              Open an Account
            </h2>
            <p className="sep-lede">
              Enrollment establishes your client identity and creates the account record that
              carries every balance reading and transfer entry on this system. Fill each field —
              values are measured exactly as entered.
            </p>
          </header>

          <form className="sep-form" onSubmit={handleSubmit} noValidate>
            <div className="sep-fields">
              {FIELDS.map((field) => {
                const inputId = `sep-${field.name}`;
                const errorId = `${inputId}-error`;
                const hasError = Boolean(errors[field.name]);
                const isPassword = field.type === 'password';
                const isConfirmed = field.name === 'confirmPassword';
                const inputType = isPassword && reveal[field.name] ? 'text' : field.type;
                const value = values[field.name];

                const describedBy = hasError ? errorId : 'sep-form-hint';

                return (
                  <div className="sep-field" key={field.name}>
                    <div className="sep-label">
                      <label htmlFor={inputId}>{field.label}</label>
                      <span className="sep-label-value">
                        {isPassword && value.length > 0
                          ? `${value.length} char`
                          : isConfirmed && reveal.confirmPassword && value.length > 0
                          ? `${value.length} char`
                          : ''}
                      </span>
                    </div>

                    <div className="sep-control-wrap">
                      <input
                        id={inputId}
                        className={`sep-input${hasError ? ' is-invalid' : ''}${
                          isPassword ? ' has-reveal' : ''
                        }`}
                        name={field.name}
                        type={inputType}
                        autoComplete={field.autoComplete}
                        value={value}
                        placeholder="—"
                        onChange={(event) => handleChange(field.name, event.target.value)}
                        onBlur={() => handleBlur(field.name)}
                        aria-invalid={hasError}
                        aria-describedby={describedBy}
                        disabled={submitting || succeeded}
                      />
                      {isPassword ? (
                        <button
                          type="button"
                          className="sep-reveal"
                          aria-pressed={reveal[field.name]}
                          aria-label={
                            reveal[field.name]
                              ? `Hide ${field.label}`
                              : `Show ${field.label}`
                          }
                          onClick={() =>
                            setReveal((prev) => ({ ...prev, [field.name]: !prev[field.name] }))
                          }
                        >
                          {reveal[field.name] ? 'Hide' : 'Show'}
                        </button>
                      ) : null}
                    </div>

                    {hasError ? (
                      <p className="sep-error" id={errorId} role="alert">
                        {errors[field.name]}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <p className="sep-submit-note" id="sep-form-hint">
              Passwords are set by you and used to verify returning access. Confirm the password
              exactly to submit.
            </p>

            {status === 'error' && submissionError ? (
              <div className="sep-result is-failure" role="alert">
                <p className="sep-result-head">Submission Failed</p>
                <p className="sep-result-body">{submissionError}</p>
              </div>
            ) : null}

            {status === 'unavailable' ? (
              <div className="sep-result" role="status">
                <p className="sep-result-body">{submissionError}</p>
              </div>
            ) : null}

            {succeeded ? (
              <div className="sep-result is-success" role="status">
                <p className="sep-result-head">Account Created</p>
                <p className="sep-result-body">
                  Your client identity is established and the account record
                  {createdId ? ` ${createdId}` : ''} is on file
                  {typeof data.length === 'number' ? ` — ${data.length} account records on this system` : ''}
                  .
                </p>
                <a className="sep-continue" href="/dashboard">
                  Continue to Dashboard
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            ) : null}

            <div className="sep-submit-zone">
              <button
                type="submit"
                className="sep-submit"
                disabled={submitting || succeeded}
                aria-busy={submitting}
              >
                <span className="sep-submit-label">
                  {submitting ? (
                    <>
                      <span className="sep-spinner" aria-hidden="true" />
                      Creating account
                    </>
                  ) : succeeded ? (
                    'Account created'
                  ) : (
                    'Create account'
                  )}
                </span>
                <span className="sep-submit-glyph" aria-hidden="true">
                  {submitting ? '···' : '→'}
                </span>
              </button>
            </div>
          </form>
        </div>

        <aside className="sep-open" aria-label="Enrollment context">
          <div className="sep-dial" aria-hidden="true">
            <span className="sep-dial-needle" />
            <span className="sep-dial-hub" />
          </div>

          <ul className="sep-readouts">
            {READOUTS.map((item) => (
              <li className="sep-readout" key={item.key}>
                <span className="sep-readout-key">{item.key}</span>
                <span className="sep-readout-val">{item.value}</span>
              </li>
            ))}
          </ul>

          <div className="sep-open-foot">
            <p className="sep-open-note">Existing client? Verify your identity to reach the ledger.</p>
            <a className="sep-open-signin" href="/login">
              Sign in
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}